<template>
  <div class="nav-bar" style="background-color: white">
    <!-- Refresh Button Header -->
    <div class="refresh-header">
      <div class="header-info">
        <h2 class="title is-5">WeServe Orders - {{ date }}</h2>
      </div>
      <b-button
        type="is-info"
        icon-left="sync-alt"
        :loading="isLoading"
        @click="refreshOrders"
      >
        Refresh
      </b-button>
    </div>

    <!-- Order List -->
    <div v-for="statusKey in nameTab" :key="statusKey">
      <div v-if="orderList[statusKey] && orderList[statusKey].length > 0" class="status-section">
        <div class="status-header">
          <h3 class="title is-5">
            <b-tag :type="getStatusHeaderType(statusKey)" size="is-medium">
              {{ formatStatusName(statusKey) }}
            </b-tag>
            <span class="count-badge">{{ orderList[statusKey].length }}</span>
          </h3>
        </div>
        <div v-for="item in orderList[statusKey]" :key="item.id">
          <CardWeServe
            class="weserve-card-style"
            :weserveID="item.id"
            :villaID="item.villaOrderID"
            :status="item.status"
            :orderCode="item.weserveOrderCode"
            :updatedAt="item.updatedAt"
            :provinceName="item.provinceName"
            @order-cancelled="handleOrderCancelled"
          />
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="isEmptyOrderList" class="empty-state">
      <p class="has-text-grey has-text-centered">
        <b-icon icon="inbox" size="is-large"></b-icon>
        <br />
        No orders found for {{ date }}
      </p>
    </div>
  </div>
</template>

<script>
import dayjs from "dayjs";

export default {
  name: "WeServeOrder",
  components: {
    CardWeServe: () => import("@/components/CardWeServe"),
  },
  data() {
    return {
      msg: "WESERVE",
      date: dayjs().subtract(7, "hour").format("YYYY-MM-DD"),
      isLoading: true,
      activeTab: 0,
      nameTab: ["ORDER_MADE", "ASSIGNING_DRIVER", "DRIVER_ASSIGNED", "ON_THE_WAY", "PICKED_UP", "COMPLETED", "CANCELLED", "REJECTED"],
    };
  },
  async mounted() {
    console.log("Hi WeServe");
    this.$store.commit("weserve/setEmptyOrders");
    await this.getOrderTaskWaiting();
  },
  computed: {
    orderList() {
      return this.$store.getters["weserve/orders"];
    },
    msgUpdateOrderStatus() {
      return this.$store.getters["weserve/updateWeServeOrderStatus"];
    },
    isEmptyOrderList() {
      return Object.keys(this.orderList).length === 0 && !this.isLoading;
    },
  },
  watch: {
    msgUpdateOrderStatus: {
      deep: true,
      handler: async function (value) {
        console.log("Watch MSG Update WeServe Order", value);
        if (value) {
          // Handle status updates
          this.$store.commit("weserve/setUpdateWeServeOrderStatus", null);
        }
      },
    },
  },
  beforeDestroy() {
    // Clean up
  },
  methods: {
    async getOrderTaskWaiting() {
      this.nameTab.forEach(async (status) => {
        await this.$store.dispatch("weserve/getWeServeOrders", {
          date: this.date,
          status: status,
        });
      });

      // Subscribe to updates
      await this.$store.dispatch("weserve/onCreateWeServeOrder");
      await this.$store.dispatch("weserve/onUpdateWeServeOrder");

      this.isLoading = false;
    },
    async refreshOrders() {
      this.isLoading = true;

      // Clear existing orders
      this.$store.commit("weserve/setEmptyOrders");

      // Reload all orders
      const promises = this.nameTab.map((status) => {
        return this.$store.dispatch("weserve/getWeServeOrders", {
          date: this.date,
          status: status,
        });
      });

      await Promise.all(promises);

      this.isLoading = false;

      this.$buefy.toast.open({
        message: "Orders refreshed successfully",
        type: "is-success",
        duration: 2000,
        position: "is-top",
      });
    },
    async handleOrderCancelled(orderData) {
      console.log("Order cancelled, refreshing list:", orderData);

      // Refresh CANCELLED status orders
      await this.$store.dispatch("weserve/getWeServeOrders", {
        date: this.date,
        status: "CANCELLED",
      });

      // Also refresh the previous status to remove the cancelled order from old list
      // This will be handled by the subscription, but we can force refresh
      this.nameTab.forEach(async (status) => {
        if (status !== "CANCELLED") {
          await this.$store.dispatch("weserve/getWeServeOrders", {
            date: this.date,
            status: status,
          });
        }
      });
    },
    formatStatusName(status) {
      return status.replace(/_/g, " ");
    },
    getStatusHeaderType(status) {
      const typeMap = {
        ORDER_MADE: "is-warning",
        ASSIGNING_DRIVER: "is-warning",
        DRIVER_ASSIGNED: "is-info",
        DRIVER_ACCEPTED: "is-info",
        ON_THE_WAY: "is-info",
        ARRIVED: "is-primary",
        PICKED_UP: "is-primary",
        COMPLETED: "is-success",
        CANCELLED: "is-danger",
        REJECTED: "is-danger"
      };
      return typeMap[status] || "is-light";
    },
  },
};
</script>

<style scoped>
.nav-bar {
  height: calc(100vh - 4.25rem);
  overflow-y: auto;
  overflow-x: hidden;
  background-color: white;
}

/* Refresh Header */
.refresh-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  position: sticky;
  top: 0;
  z-index: 20;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.refresh-header .header-info {
  display: flex;
  align-items: center;
}

.refresh-header .header-info .title {
  color: white;
  margin-bottom: 0 !important;
  line-height: 1.2;
}

/* Empty State */
.empty-state {
  padding: 60px 20px;
  text-align: center;
}

/* Status Section Styles */
.status-section {
  margin-bottom: 30px;
  padding: 0 20px;
}

.status-header {
  padding: 10px 15px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-radius: 8px 8px 0 0;
  margin-bottom: 15px;
  position: sticky;
  top: 60px;
  z-index: 10;
}

.status-header .title {
  display: flex;
  align-items: center;
  margin-bottom: 0;
}

.count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: white;
  color: #363636;
  font-weight: bold;
  font-size: 0.9rem;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  margin-left: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.weserve-card-style {
  margin-bottom: 15px;
}

/* Custom scrollbar */
.nav-bar::-webkit-scrollbar {
  width: 8px;
}

.nav-bar::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.nav-bar::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.nav-bar::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>